import { useState, useEffect, useRef } from "react";
import { Bell, Package, Truck, CheckCircle2, UserCheck, X } from "lucide-react";
import useNotificationStore from "../store/notificationStore";
import api from "../services/api";
import useAuthStore from "../store/authStore";
import { toast } from "sonner";

const iconMap = {
  new_order: Package,
  order_update: Truck,
  order_created: CheckCircle2,
};

function timeAgo(dateStr) {
  const s = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, fetchNotifications, fetchUnreadCount, markAllRead } = useNotificationStore();
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [accepting, setAccepting] = useState(null);
  const [acceptedOrders, setAcceptedOrders] = useState(new Set());
  const ref = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    fetchNotifications();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleAccept = async (trackingId) => {
    setAccepting(trackingId);
    try {
      await api.post(`/orders/accept/${trackingId}`);
      toast.success(`Accepted order ${trackingId}!`);
      setAcceptedOrders((prev) => new Set([...prev, trackingId]));
      fetchNotifications();
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not accept order";
      if (msg.includes("already accepted")) {
        setAcceptedOrders((prev) => new Set([...prev, trackingId]));
      }
      toast.error(msg);
    } finally {
      setAccepting(null);
    }
  };

  const handleOpen = () => {
    setOpen(!open);
    if (!open) {
      fetchNotifications();
      if (unreadCount > 0) markAllRead();
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#FF6B00] text-white text-[10px] font-bold rounded-full flex items-center justify-center live-dot" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50 animate-scaleIn" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <h3 className="font-['Outfit'] text-sm font-semibold text-slate-900">Notifications</h3>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 20).map((n, i) => {
                const Icon = iconMap[n.type] || Bell;
                const isNewOrder = n.type === "new_order" && user?.role === "agent";
                return (
                  <div
                    key={n.tracking_id ? `${n.tracking_id}-${i}` : i}
                    className={`px-4 py-3 border-b border-slate-50 ${!n.read ? "bg-orange-50/40" : ""} hover:bg-slate-50/80 transition-colors`}
                    data-testid={`notification-item-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        n.type === "new_order" ? "bg-blue-50" : n.type === "order_update" ? "bg-orange-50" : "bg-green-50"
                      }`}>
                        <Icon className={`h-4 w-4 ${
                          n.type === "new_order" ? "text-blue-600" : n.type === "order_update" ? "text-[#FF6B00]" : "text-green-600"
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
                        {n.tracking_id && <p className="text-[10px] font-mono text-slate-400 mt-0.5">{n.tracking_id}</p>}
                        {isNewOrder && (n.data?.customer_name || n.customer_name) && (
                          <p className="text-[10px] text-blue-600 font-medium mt-0.5">
                            Customer: {n.data?.customer_name || n.customer_name}{(n.data?.customer_phone || n.customer_phone) ? ` (${n.data?.customer_phone || n.customer_phone})` : ''}
                          </p>
                        )}
                        <p className="text-[10px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                        {isNewOrder && (n.data?.tracking_id || n.tracking_id) && !acceptedOrders.has(n.data?.tracking_id || n.tracking_id) && (
                          <button
                            onClick={() => handleAccept(n.data?.tracking_id || n.tracking_id)}
                            disabled={accepting === (n.data?.tracking_id || n.tracking_id)}
                            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF6B00] text-white text-xs font-semibold rounded-lg hover:bg-[#E65A00] btn-press disabled:opacity-50"
                            data-testid={`accept-order-${n.data?.tracking_id || n.tracking_id}`}
                          >
                            <UserCheck className="h-3 w-3" />
                            {accepting === (n.data?.tracking_id || n.tracking_id) ? "Accepting..." : "Accept Pickup"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
